/**
 * SessionStatusBar — Unified status indicator shown at the bottom of the middle column.
 * Visible across all tabs (Transcript, Notes, Summary).
 *
 * Priority order (first match wins):
 * 1. Recording → "Generating transcript..."
 * 2. WS connected (LLM analyzing live) → "Analyzing transcript..."
 * 3. First task from running_tasks array (if not stale) → task-specific label
 * 4. Default → word count (transcript tab only)
 *
 * Tasks older than 2 minutes (based on updated_at) are considered stale and ignored.
 */

import React from 'react';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import { useAppSelector } from '../../../store/store';

/** Display labels for each running task type */
const TASK_LABELS: Record<string, string> = {
  postprocessing: 'Enhancing transcript...',
  analyzing: 'Analyzing...',
  extracting_evidence: 'Extracting evidence...',
  generating_summary: 'Generating summary...',
};

/** Tasks with no update for 2+ minutes are considered stale */
const STALE_THRESHOLD_MS = 2 * 60 * 1000;

interface SessionStatusBarProps {
  /** Current active tab — controls background color and default text */
  activeTab: 'transcript' | 'notes' | 'summary';
}

const SessionStatusBar: React.FC<SessionStatusBarProps> = ({ activeTab }) => {
  const { currentSession } = useAppSelector((state) => state.session);
  const { isRecording, wsStatus } = useAppSelector((state) => state.recording);

  const runningTasks = currentSession?.running_tasks ?? [];

  // Check if running_tasks are stale (updated_at hasn't changed in 2+ min)
  const isStale = runningTasks.length > 0
    && currentSession?.updated_at
    && (Date.now() - new Date(currentSession.updated_at).getTime()) > STALE_THRESHOLD_MS;

  // First task in the array (oldest/most relevant), or null if stale
  const activeTask = (!isStale && runningTasks.length > 0) ? runningTasks[0] : null;

  // Word count from session full_text
  const wordCount = React.useMemo(() => {
    const text = currentSession?.full_text || '';
    return text.split(/\s+/).filter((w) => w.length > 0).length;
  }, [currentSession?.full_text]);

  // Determine what to show (priority order)
  let statusText: string | null = null;
  let showLoader = false;

  if (isRecording) {
    statusText = 'Generating transcript...';
    showLoader = true;
  } else if (wsStatus === 'connected' && !isRecording) {
    statusText = 'Analyzing transcript...';
    showLoader = true;
  } else if (activeTask && TASK_LABELS[activeTask]) {
    statusText = TASK_LABELS[activeTask];
    showLoader = true;
  }

  // Background matches the active tab's background
  const backgroundColor = activeTab === 'notes' ? '#f8f8f8' : '#fff';

  // Nothing to show and not on transcript tab
  const hasContent = Boolean(currentSession?.full_text);
  if (!statusText && (activeTab !== 'transcript' || !hasContent)) {
    return null;
  }

  return (
    <div
      style={{
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        backgroundColor,
        borderTop: activeTab === 'notes' ? 'none' : undefined,
      }}
    >
      {statusText ? (
        <Text type="h50" color="secondary">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              verticalAlign: 'middle',
            }}
          >
            {showLoader && <Loader size="small" />}
            <span>{statusText}</span>
          </span>
        </Text>
      ) : (
        // Default: word count (only on transcript tab with content)
        <Text type="h50" color="secondary">
          {wordCount} words
        </Text>
      )}
    </div>
  );
};

export default SessionStatusBar;
